import React, { useState, useEffect } from 'react';
import './preFacShipmentSelector.css';
import {
  fetchShipmentsByFilters,
  generateInvoicePDF,
  createInvoice,
  fetchClientByCode,
  fetchAvailableCredits,
} from '../../services/api';
import LoadingButton from '../LoadingButton/LoadingButton';
import EmailSelector from '../EmailSelector/EmailSelector';
import MessageBox from '../MessageBox/MessageBox';
import DateInput from '../DateInput/DateInput';
import { createErrorMessageBoxConfig } from '../../utils/errorHandler';

export default function PreFacShipmentSelector({
  clientValue,
  period,
  month,
  shipment_filters,
  onClose,
  onConfirm,
  onCancel,
}) {
  const [shipments, setShipments] = useState([]);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [showMailSelector, setShowMailSelector] = useState(false);
  const [availableCredits, setAvailableCredits] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [messageBoxConfig, setMessageBoxConfig] = useState({
    message: '',
    type: 'ok',
    onConfirm: null,
    onCancel: null,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [billingDate, setBillingDate] = useState('');

  useEffect(() => {
    const fetchData = async filters => {
      try {
        const fetchedData = await fetchShipmentsByFilters(filters, 'delivery_date');
        if (fetchedData && Array.isArray(fetchedData.results)) {
          setShipments(fetchedData.results);
        } else {
          setShipments([]);
          console.error('La réponse ne contient pas de tableau "results"');
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        setShipments([]);
      } finally {
        setDataLoaded(true);
      }
    };

    const fetchCredits = async () => {
      try {
        const fetchedData = await fetchAvailableCredits(clientValue);
        if (fetchedData && Array.isArray(fetchedData.results)) {
          setAvailableCredits(fetchedData.results.map(credit => ({ ...credit, selected: false })));
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des avoirs:', error);
      }
    };

    const filterInputs = {};

    shipment_filters.forEach((value, key) => {
      if (value !== '') {
        filterInputs[key] = value;
      }
    });

    filterInputs['invoicing_status'] = 'PRÉ-FAC';

    fetchData(filterInputs);
    fetchCredits();
  }, [clientValue, shipment_filters]);

  useEffect(() => {
    if (dataLoaded) {
      setSelectedShipments(shipments.map(shipment => ({ ...shipment, selected: true })));
    }
  }, [shipments, dataLoaded]);

  const handleSelectAll = () => {
    setSelectedShipments(selectedShipments.map(shipment => ({ ...shipment, selected: true })));
  };

  const handleDeselectAll = () => {
    setSelectedShipments(selectedShipments.map(shipment => ({ ...shipment, selected: false })));
  };

  const handleToggleSelect = index => {
    const newShipments = [...selectedShipments];
    newShipments[index].selected = !newShipments[index].selected;
    setSelectedShipments(newShipments);
  };

  const handleToggleCreditSelect = index => {
    const newCredits = [...availableCredits];
    newCredits[index].selected = !newCredits[index].selected;
    setAvailableCredits(newCredits);
  };

  const getSelectedCredits = () => {
    return availableCredits.filter(credit => credit.selected);
  };

  const getSelectedCreditsSum = () => {
    return availableCredits
      .filter(credit => credit.selected)
      .reduce((sum, credit) => sum + parseFloat(credit.total), 0);
  };

  const getSelectedShipmentsSum = () => {
    return selectedShipments
      .filter(shipment => shipment.selected)
      .reduce((sum, shipment) => sum + parseFloat(shipment.client_price || 0), 0);
  };

  const onTriggerConfirm = async () => {
    const fetchInvoicePDF = async (selected_shipments, selected_credits) => {
      try {
        const response = await generateInvoicePDF(
          clientValue,
          selected_shipments,
          'invoice',
          selected_credits,
          showPreview,
          billingDate || null
        );
        return response;
      } catch (error) {
        setMessageBoxConfig(createErrorMessageBoxConfig(error, () => setShowMessageBox(false)));
        setShowMessageBox(true);
      }
    };

    const selected = selectedShipments.filter(shipment => shipment.selected);
    const selectedCredits = getSelectedCredits();
    const response = await fetchInvoicePDF(selected, selectedCredits);

    if (response) {
      setShowMailSelector(true);
    }
  };

  const handleConfirmEmailSelector = async email_list => {
    const selectedCredits = getSelectedCredits();
    const selected = selectedShipments.filter(shipment => shipment.selected);

    const CreateInvoice = async (shipmentsToInvoice, email_list, credits) => {
      try {
        const response = await createInvoice(
          clientValue,
          shipmentsToInvoice,
          'invoice',
          email_list,
          period,
          month,
          '',
          credits,
          billingDate || null
        );
        return response;
      } catch (error) {
        setMessageBoxConfig(createErrorMessageBoxConfig(error, () => setShowMessageBox(false)));
        setShowMessageBox(true);
      }
    };

    const response = await CreateInvoice(selected, email_list, selectedCredits);

    if (response) {
      const { email_sent } = response;

      if (email_sent) {
        onConfirm();
      } else {
        setMessageBoxConfig({
          message: `L'envoi de l'email a rencontré une erreur, la facture n'a pas pu être créée. Merci de contacter un administrateur.`,
          type: 'ok',
          onConfirm: () => {
            setShowMessageBox(false);
            onConfirm();
          },
          onClose: () => {
            setShowMessageBox(false);
            onConfirm();
          },
        });
        setShowMessageBox(true);
      }
    }
    setShowMailSelector(false);
  };

  return (
    <div className="overlay">
      <div className="shipment-selector-content">
        <span className="overlay-close" onClick={onClose}>
          &times;
        </span>

        {/* Grille principale */}
        <div className="selector-grid">
          {/* Colonne de gauche : listes */}
          <div className="selector-lists">
            {/* Section dossiers */}
            <p className="selector-section-title">Dossiers à facturer</p>
            <div className="shipments-list">
              <table>
                <tbody>
                  {selectedShipments.map((shipment, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={shipment.selected}
                          onChange={() => handleToggleSelect(index)}
                        />
                      </td>
                      <td onClick={() => handleToggleSelect(index)}>
                        {`Dossier ${shipment.shipment_number}`}
                      </td>
                      <td onClick={() => handleToggleSelect(index)}>
                        {`Liv: ${shipment.delivery_date}`}
                      </td>
                      <td
                        onClick={() => handleToggleSelect(index)}
                      >{`${shipment.client_price}€`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section avoirs (affiché seulement s'il y en a) */}
            {availableCredits.length > 0 && (
              <>
                <p className="selector-section-title credits-title">Avoirs disponibles</p>
                <div className="shipments-list credits-list">
                  <table>
                    <tbody>
                      {availableCredits.map((credit, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="checkbox"
                              checked={credit.selected}
                              onChange={() => handleToggleCreditSelect(index)}
                            />
                          </td>
                          <td onClick={() => handleToggleCreditSelect(index)}>
                            {credit.invoice_number}
                          </td>
                          <td onClick={() => handleToggleCreditSelect(index)}>{credit.total}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Colonne de droite : boutons */}
          <div className="select-all-buttons">
            <button type="button" onClick={handleSelectAll}>
              Tout sélectionner
            </button>
            <button type="button" onClick={handleDeselectAll}>
              Tout désélectionner
            </button>
          </div>
        </div>

        {/* Récapitulatif des totaux */}
        <div className="totals-summary">
          <div className="total-row">
            <span>Total dossiers :</span>
            <span>{getSelectedShipmentsSum().toFixed(2)}€</span>
          </div>
          {availableCredits.length > 0 && getSelectedCreditsSum() !== 0 && (
            <>
              <div className="total-row">
                <span>Total avoirs :</span>
                <span>{getSelectedCreditsSum().toFixed(2)}€</span>
              </div>
              <div className="total-row total-final">
                <span>Total facture :</span>
                <span>{(getSelectedShipmentsSum() + getSelectedCreditsSum()).toFixed(2)}€</span>
              </div>
            </>
          )}
        </div>

        {/* Options et Boutons */}
        <div className="shipment-selector-options">
          <label className="preview-checkbox">
            <input
              type="checkbox"
              checked={showPreview}
              onChange={e => setShowPreview(e.target.checked)}
            />
            Afficher l'aperçu PDF
          </label>
          <label className="billing-date-label">
            Date de facturation :
            <DateInput
              name="billing_date"
              value={billingDate}
              onChange={e => setBillingDate(e.target.value)}
              disablePastDateWarning
              disableFutureDates
            />
          </label>
        </div>
        <div className="shipment-selector-buttons">
          <LoadingButton onClick={onTriggerConfirm}>Valider</LoadingButton>
          <button type="button" onClick={onCancel}>
            Annuler
          </button>
        </div>
      </div>

      {showMailSelector && (
        <EmailSelector
          fetchFunction={fetchClientByCode}
          code={clientValue}
          onClose={() => setShowMailSelector(false)}
          onConfirm={handleConfirmEmailSelector}
          onCancel={() => setShowMailSelector(false)}
        />
      )}

      {showMessageBox && (
        <MessageBox
          message={messageBoxConfig.message}
          onClose={() => setShowMessageBox(false)}
          onConfirm={messageBoxConfig.onConfirm}
          onCancel={messageBoxConfig.onCancel}
          type={messageBoxConfig.type}
          technicalDetails={messageBoxConfig.technicalDetails}
        />
      )}
    </div>
  );
}
